/**
 * Client Profile Hook
 * 
 * Manages client user profiles including personal information, organization details,
 * procurement preferences, and profile completion tracking. Enables clients to
 * maintain their account information and measure profile completeness.
 * 
 * **Profile Sections:**
 * 1. **Basic Info**: Name, job title, contact details, timezone
 * 2. **Organization Info**: Department, authority level
 * 3. **Procurement Preferences**: Budget range, frequency, preferred vendors
 * 4. **Profile Image**: Avatar/photo upload
 * 
 * **Profile Completion Tracking:**
 * - Automatic calculation of completion percentage
 * - Section-by-section completion flags
 * - Overall completion score (0-100%)
 * - Missing field identification
 * 
 * **Completion Criteria:**
 * - **Basic Info**: First name, last name, phone, timezone
 * - **Organization Info**: Department, job title
 * - **Procurement**: Budget range, frequency, decision authority
 * 
 * **Key Features:**
 * - Fetch current user's client profile
 * - Update profile information
 * - Calculate profile completion
 * - Track completion by section
 * - Automatic profile creation on first access
 * 
 * **Profile Completion Benefits:**
 * - Better vendor matching
 * - Improved search results
 * - Enhanced credibility
 * - Access to premium features
 * 
 * @returns {Object} Client profile management
 * @returns {ClientProfile | null} profile - Current client profile
 * @returns {ClientProfileCompletion | null} completion - Completion metrics
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} updateProfile - Update profile data
 * @returns {Function} calculateCompletion - Recalculate completion metrics
 * 
 * @example
 * // Display profile completion
 * const { profile, completion, loading } = useClientProfile();
 * 
 * if (loading) return <Spinner />;
 * 
 * return (
 *   <ProfileCard>
 *     <h2>{profile?.first_name} {profile?.last_name}</h2>
 *     <ProgressBar value={completion?.overall_percentage || 0} />
 *     {!completion?.basic_info_complete && (
 *       <Alert>Complete your basic info</Alert>
 *     )}
 *   </ProfileCard>
 * );
 * 
 * @example
 * // Update client profile
 * const { updateProfile } = useClientProfile();
 * 
 * await updateProfile({
 *   job_title: 'Procurement Manager',
 *   department: 'Operations',
 *   phone_number: '+1-555-0100',
 *   typical_budget_range: '$10k-$50k'
 * });
 * 
 * @example
 * // Check completion and prompt
 * const { completion } = useClientProfile();
 * 
 * if (completion && completion.overall_percentage < 50) {
 *   showProfileCompletionPrompt();
 * }
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export interface ClientProfile {
  id: string;
  user_id: string;
  organization_id: string;
  first_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  department?: string | null;
  phone_number?: string | null;
  timezone?: string | null;
  bio?: string | null;
  profile_image_url?: string | null;
  preferred_contact_method?: string | null;
  typical_budget_range?: string | null;
  procurement_frequency?: string | null;
  decision_authority_level?: string | null;
  preferred_vendors?: any;
  created_at: string;
  updated_at: string;
}

export interface ClientProfileCompletion {
  id: string;
  profile_id: string;
  basic_info_complete: boolean;
  procurement_complete: boolean;
  overall_percentage: number;
  created_at: string;
  updated_at: string;
}

export const useClientProfile = () => {
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [completion, setCompletion] = useState<ClientProfileCompletion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch client profile and completion
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const user = await supabase.auth.getUser();
      
      const { data: profileData, error: profileError } = await supabase
        .from('client_profiles')
        .select('*')
        .eq('user_id', user.data.user?.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setProfile(profileData as ClientProfile);

      // Fetch completion data
      if (profileData) {
        const { data: completionData } = await supabase
          .from('client_profile_completion')
          .select('*')
          .eq('profile_id', profileData.id)
          .maybeSingle();

        setCompletion(completionData as ClientProfileCompletion);
      }
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch client profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Create client profile
  const createProfile = async (profileData: Partial<ClientProfile>) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      // Get user's organization
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.data.user.id)
        .single();

      if (!userProfile?.organization_id) {
        throw new Error('User must be part of an organization');
      }

      const { data, error } = await supabase
        .from('client_profiles')
        .insert({
          user_id: user.data.user.id,
          organization_id: userProfile.organization_id,
          ...profileData,
        })
        .select('*')
        .single();

      if (error) throw error;
      
      setProfile(data as ClientProfile);
      
      toast({
        title: "Success",
        description: "Client profile created successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to create client profile",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Update client profile
  const updateProfile = async (updates: Partial<ClientProfile>) => {
    try {
      if (!profile) throw new Error('No profile to update');

      const { data, error } = await supabase
        .from('client_profiles')
        .update(updates)
        .eq('id', profile.id)
        .select('*')
        .single();

      if (error) throw error;
      
      setProfile(data as ClientProfile);
      
      // Track profile update
      analytics.track({
        name: ANALYTICS_EVENTS.PROFILE_UPDATED,
        category: 'engagement',
        properties: {
          profile_type: 'user',
        },
      });

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
      throw err;
    }
  };


  // Update completion status
  const updateCompletionStatus = async () => {
    if (!profile) return;

    const basicInfoComplete = !!(
      profile.first_name &&
      profile.last_name &&
      profile.phone_number &&
      profile.job_title
    );

    // Fetch organization profile to check company profile completion
    const { data: orgProfile } = await supabase
      .from('organization_profiles')
      .select('industry, company_size, description, annual_revenue_range, street_address, city, state, zip_code')
      .eq('organization_id', profile.organization_id)
      .maybeSingle();

    const companyProfileComplete = !!(
      orgProfile?.industry &&
      orgProfile?.company_size &&
      orgProfile?.description &&
      orgProfile?.street_address &&
      orgProfile?.city &&
      orgProfile?.state &&
      orgProfile?.zip_code
    );

    const completedSections = (basicInfoComplete ? 1 : 0) + (companyProfileComplete ? 1 : 0);
    const totalSections = 2;

    const overallPercentage = Math.round((completedSections / totalSections) * 100);

    try {
      const { data, error } = await supabase
        .from('client_profile_completion')
        .upsert({
          profile_id: profile.id,
          basic_info_complete: basicInfoComplete,
          procurement_complete: companyProfileComplete, // Repurposed column for company profile
          overall_percentage: overallPercentage,
        }, { onConflict: 'profile_id' })
        .select()
        .single();

      if (error) throw error;
      setCompletion(data as ClientProfileCompletion);
    } catch (err: any) {
      console.error('Failed to update completion status:', err);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      updateCompletionStatus();
    }
  }, [profile]);

  return {
    profile,
    completion,
    loading,
    error,
    fetchProfile,
    createProfile,
    updateProfile,
    updateCompletionStatus,
  };
};